import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase, FeedbackRequest } from '../lib/supabase';
import { Plus, LogOut, MessageSquare, Clock, CheckCircle2, Mail, Settings as SettingsIcon, CreditCard, TrendingUp, Users, Mic, Activity, Sprout, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import RequestFeedbackModal from './RequestFeedbackModal';
import FeedbackSummary from './FeedbackSummary';
import AnalyticsView from './AnalyticsView';
import OnboardingTour from './OnboardingTour';
import { checkUsageLimit } from '../lib/subscription-service';
import { useToast } from '../contexts/ToastContext';
import { checkManagerAccess } from '../lib/manager-service';

export default function Dashboard() {
  const { profile, signOut, user, loading: authLoading, profileLoading, isProfileComplete } = useAuth();
  const navigate = useNavigate();
  const { showWarning } = useToast();
  const [requests, setRequests] = useState<FeedbackRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'requests' | 'summary' | 'analytics'>('requests');
  const [usageInfo, setUsageInfo] = useState<{
    current: number;
    limit: number;
    planType: string;
  }>({ current: 0, limit: 5, planType: 'free' });
  const [hasManagerAccess, setHasManagerAccess] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitMessage, setLimitMessage] = useState('');

  useEffect(() => {
    console.log('[Dashboard] State:', {
      user: !!user,
      authLoading,
      profileLoading,
      isProfileComplete,
      timestamp: new Date().toISOString()
    });

    // CRITICAL: Wait for BOTH auth AND profile to finish loading completely
    if (authLoading || profileLoading) {
      console.log('[Dashboard] Waiting for auth/profile to load...');
      return;
    }

    // After loading is complete, check profile completion with fresh data
    if (!isProfileComplete) {
      console.log('[Dashboard] ❌ Profile incomplete - redirecting to profile setup');
      navigate('/profile', { replace: true });
      return;
    }

    // Profile is verified complete, load dashboard data
    console.log('[Dashboard] ✓ Profile complete - loading dashboard data');
    loadRequests();
    loadUsageInfo();
    checkManager();
    checkOnboardingStatus();
  }, [user, authLoading, profileLoading, isProfileComplete, navigate]);

  const checkOnboardingStatus = () => {
    const hasSeenTour = localStorage.getItem('mentra_tour_completed');
    if (!hasSeenTour && requests.length === 0) {
      setTimeout(() => setShowOnboarding(true), 1000);
    }
  };

  const handleOnboardingComplete = () => {
    localStorage.setItem('mentra_tour_completed', 'true');
    setShowOnboarding(false);
  };

  const handleOnboardingSkip = () => {
    localStorage.setItem('mentra_tour_completed', 'true');
    setShowOnboarding(false);
  };

  const checkManager = async () => {
    if (!user) return;
    try {
      const access = await checkManagerAccess(user.id);
      setHasManagerAccess(access.hasAccess);
    } catch (error) {
      console.error('Error checking manager access:', error);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/', { replace: true });
  };

  const loadRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('feedback_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRequests(data || []);
    } catch (error) {
      console.error('Error loading requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsageInfo = async () => {
    if (!user) return;
    try {
      const usage = await checkUsageLimit(user.id, 'requests');
      setUsageInfo(usage);
    } catch (error) {
      console.error('Error loading usage:', error);
    }
  };

  const handleRequestFeedback = async () => {
    if (!user) return;

    const usage = await checkUsageLimit(user.id, 'requests');
    if (!usage.canProceed) {
      setLimitMessage(`You have reached your Free plan limit of ${usage.limit} total feedback requests. Please upgrade to continue creating requests.`);
      setShowLimitModal(true);
      return;
    }

    setShowRequestModal(true);
  };

  const handleUpgradeClick = () => {
    setShowLimitModal(false);
    navigate('/pricing');
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      expired: 'bg-gray-100 text-gray-800',
    };
    return styles[status as keyof typeof styles] || styles.pending;
  };

  const getStatusIcon = (status: string) => {
    if (status === 'completed') return <CheckCircle2 className="w-4 h-4" />;
    if (status === 'expired') return <Clock className="w-4 h-4" />;
    return <Clock className="w-4 h-4" />;
  };

  const completedRequests = requests.filter(r => r.status === 'completed').length;
  const pendingRequests = requests.filter(r => r.status === 'pending').length;
  const responseRate = requests.length > 0 
    ? Math.round((completedRequests / requests.length) * 100) 
    : 0;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-[#8CAFA8] w-10 h-10 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" strokeWidth={2} />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-[#1F2937]">Mentra</h1>
                <p className="text-sm text-[#6B7280]">Welcome back, {profile?.full_name || 'User'}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/pricing')}
                className="flex items-center gap-2 px-3 py-2 text-[#6B7280] hover:text-[#1F2937] transition"
              >
                <CreditCard className="w-4 h-4" strokeWidth={1.5} />
                <span className="hidden md:inline text-sm">Upgrade</span>
              </button>
              <button
                onClick={() => navigate('/settings')}
                className="flex items-center gap-2 px-3 py-2 text-[#6B7280] hover:text-[#1F2937] transition"
              >
                <SettingsIcon className="w-4 h-4" strokeWidth={1.5} />
                <span className="hidden md:inline text-sm">Settings</span>
              </button>
              <button
                onClick={handleSignOut}
                className="flex items-center gap-2 px-3 py-2 text-[#6B7280] hover:text-[#1F2937] transition"
              >
                <LogOut className="w-4 h-4" strokeWidth={1.5} />
                <span className="hidden md:inline text-sm">Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-7xl">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-[#8CAFA8] to-[#7A9B94] rounded-[24px] p-8 md:p-10 mb-8 text-white">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold mb-3">Your Feedback Hub</h2>
              <p id="usage-info" className="text-lg text-white/90 mb-6">
                {usageInfo.current} of {usageInfo.limit} requests used this month
              </p>
              <div className="w-full max-w-2xl bg-white/20 rounded-full h-2.5 overflow-hidden">
                <div
                  className="bg-white h-full rounded-full transition-all duration-300"
                  style={{ width: `${(usageInfo.current / usageInfo.limit) * 100}%` }}
                />
              </div>
            </div>
            <div className="text-right">
              <div className="text-5xl md:text-6xl font-semibold mb-1">{completedRequests}</div>
              <div className="text-lg text-white/90">Completed</div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-[16px] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-[#6B7280]">Total Requests</h3>
              <MessageSquare className="w-5 h-5 text-[#9CA3AF]" strokeWidth={1.5} />
            </div>
            <div className="text-3xl font-semibold text-[#1F2937]">{requests.length}</div>
          </div>

          <div className="bg-white rounded-[16px] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-[#6B7280]">Pending</h3>
              <Clock className="w-5 h-5 text-[#F59E0B]" strokeWidth={1.5} />
            </div>
            <div className="text-3xl font-semibold text-[#F59E0B]">{pendingRequests}</div>
          </div>

          <div className="bg-white rounded-[16px] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-[#6B7280]">Response Rate</h3>
              <TrendingUp className="w-5 h-5 text-[#10B981]" strokeWidth={1.5} />
            </div>
            <div className="text-3xl font-semibold text-[#10B981]">{responseRate}%</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-between mb-6 border-b border-gray-200">
          <div className="flex gap-8">
            <button
              id="requests-tab"
              onClick={() => setActiveTab('requests')}
              className={`pb-3 px-1 font-medium text-sm transition border-b-2 ${
                activeTab === 'requests'
                  ? 'text-[#8CAFA8] border-[#8CAFA8]'
                  : 'text-[#6B7280] border-transparent hover:text-[#1F2937]'
              }`}
            >
              Feedback Requests
            </button>
            <button
              id="insights-tab"
              onClick={() => setActiveTab('summary')}
              className={`pb-3 px-1 font-medium text-sm transition border-b-2 ${
                activeTab === 'summary'
                  ? 'text-[#8CAFA8] border-[#8CAFA8]'
                  : 'text-[#6B7280] border-transparent hover:text-[#1F2937]'
              }`}
            >
              AI Insights
            </button>
            <button
              id="analytics-tab"
              onClick={() => setActiveTab('analytics')}
              className={`pb-3 px-1 font-medium text-sm transition border-b-2 ${
                activeTab === 'analytics'
                  ? 'text-[#8CAFA8] border-[#8CAFA8]'
                  : 'text-[#6B7280] border-transparent hover:text-[#1F2937]'
              }`}
            >
              Analytics
            </button>
          </div>
          <button
            id="request-feedback-btn"
            onClick={handleRequestFeedback}
            className="bg-[#8CAFA8] text-white px-5 py-2.5 rounded-lg font-medium hover:bg-[#7A9B94] transition shadow-sm flex items-center gap-2"
          >
            <Plus className="w-4 h-4" strokeWidth={2} />
            Request Feedback
          </button>
        </div>

        {/* Content Area */}
        {activeTab === 'requests' && (
          <div className="space-y-4">
            {loading ? (
              <div className="bg-white rounded-[16px] p-8 text-center">
                <div className="text-[#6B7280]">Loading...</div>
              </div>
            ) : requests.length === 0 ? (
              <div className="bg-white rounded-[16px] p-12 text-center">
                <MessageSquare className="w-12 h-12 text-[#9CA3AF] mx-auto mb-4" strokeWidth={1.5} />
                <h3 className="text-lg font-semibold text-[#1F2937] mb-2">No feedback requests yet</h3>
                <p className="text-[#6B7280] mb-6">Start your growth journey by requesting feedback from colleagues</p>
                <button
                  onClick={handleRequestFeedback}
                  className="bg-[#8CAFA8] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#7A9B94] transition inline-flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Request Your First Feedback
                </button>
              </div>
            ) : (
              requests.map((request) => (
                <div key={request.id} className="bg-white rounded-[16px] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)] transition">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-[#1F2937] mb-1">
                        {request.recipient_email || 'No email provided'}
                      </h3>
                      <p className="text-sm text-[#6B7280]">{request.recipient_email}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 ${
                      request.status === 'completed'
                        ? 'bg-[#D1FAE5] text-[#065F46]'
                        : 'bg-[#FEF3C7] text-[#92400E]'
                    }`}>
                      <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} />
                      {request.status}
                    </span>
                  </div>

                  <div className="bg-[#F9FAFB] rounded-lg p-4 mb-4">
                    <p className="text-[#1F2937] text-sm leading-relaxed">
                      {request.message || 'No message provided'}
                    </p>
                  </div>

                  <div className="text-xs text-[#6B7280]">
                    Sent {new Date(request.created_at).toLocaleDateString('en-US', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'summary' && (
          <FeedbackSummary />
        )}

        {activeTab === 'analytics' && (
          <AnalyticsView />
        )}
      </main>

      {showRequestModal && (
        <RequestFeedbackModal
          onClose={() => setShowRequestModal(false)}
          onSuccess={() => {
            setShowRequestModal(false);
            loadRequests();
            loadUsageInfo();
          }}
        />
      )}

      {showLimitModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[24px] max-w-md w-full shadow-2xl p-8 text-center">
            <AlertCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-[#1F2937] mb-2">Plan Limit Reached</h2>
            <p className="text-[#6B7280] mb-6">
              {limitMessage}
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setShowLimitModal(false)}
                className="px-6 py-3 border border-gray-300 text-[#6B7280] rounded-lg font-semibold hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleUpgradeClick}
                className="px-6 py-3 bg-[#8CAFA8] text-white rounded-lg font-semibold hover:bg-[#7A9B94] transition"
              >
                Upgrade Plan
              </button>
            </div>
          </div>
        </div>
      )}

      {showOnboarding && (
        <OnboardingTour
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
        />
      )}
    </div>
  );
}
